import { Alert, AlertColor, Snackbar as MuiSnackBar } from "@mui/material";
import React from "react";
import { useEffect, useState } from "react";
import { FormErrorBanner, theme } from "@compass/core";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import Zoom from '@mui/material/Zoom';
import { createPortal } from "react-dom";

export interface ActionMessage {
  key: number;
  title?: string;
  message: string;
  alertColor: AlertColor;
}

export interface State {
  open: boolean;
  snackPack: readonly ActionMessage[];
  messageInfo?: ActionMessage;
}

export const ActionMessage = () => {
  const toast = useSelector((state: RootState) => state.app.toast);
  const [snackPack, setSnackPack] = useState<readonly ActionMessage[]>([]);
  const [open, setOpen] = useState(false);
  const [messageInfo, setMessageInfo] = useState<ActionMessage | undefined>(undefined);
  const [inlineError, setInlineError] = useState<ActionMessage | undefined>(undefined);
  const [inlineTarget, setInlineTarget] = useState<Element | undefined>(undefined);
  const [fieldErrorMessages, setFieldErrorMessages] = useState<string[]>([]);

  useEffect(() => {
    if (snackPack.length && !messageInfo) {
      // Set a new snack when we don't have an active one
      setMessageInfo({ ...snackPack[0] });
      setSnackPack((prev) => prev.slice(1));
      setOpen(true);
    } else if (snackPack.length && messageInfo && open) {
      // Close an active snack when a new one is added
      setOpen(false);
    }
  }, [snackPack, messageInfo, open]);

  useEffect(() => {
    if (toast) {
      const message = {
        key: toast.timeSent,
        title: toast.title,
        message: toast.message,
        alertColor: toast.alertColor,
      };

      if (toast.alertColor === "error") {
        const slots = Array.from(document.querySelectorAll('[data-form-error-banner-slot="true"]'));
        const visibleSlots = slots.filter((slot) => (slot as HTMLElement).offsetParent !== null);

        setFieldErrorMessages([]);
        setInlineTarget(visibleSlots[visibleSlots.length - 1] ?? slots[slots.length - 1]);
        setInlineError(message);
        return;
      }

      setFieldErrorMessages([]);
      setInlineError(undefined);
      setInlineTarget(undefined);
      setSnackPack((prev) => [...prev, message]);
    }
  }, [toast]);

  useEffect(() => {
    if (!inlineError) {
      return;
    }

    const clearOnRetry = (event: MouseEvent) => {
      const button = (event.target as Element | null)?.closest("button");
      const buttonText = button?.textContent?.trim() ?? "";
      const isRetryAction =
        button?.type === "submit" ||
        /\b(save|submit|retry|update|create|generate|next)\b/i.test(buttonText);

      if (button && button.getAttribute("aria-label") !== "Dismiss error" && isRetryAction) {
        setFieldErrorMessages([]);
        setInlineError(undefined);
        setInlineTarget(undefined);
      }
    };

    document.addEventListener("click", clearOnRetry, true);
    return () => document.removeEventListener("click", clearOnRetry, true);
  }, [inlineError]);

  useEffect(() => {
    if (!inlineError || !inlineTarget) {
      return;
    }

    const scrollToFirstInvalidField = () => {
      const scope =
        inlineTarget.closest(".MuiDialog-paper") ??
        inlineTarget.closest("form") ??
        document;
      const candidates = Array.from(
        scope.querySelectorAll<HTMLElement>(
          [
            '[aria-invalid="true"]',
            ".Mui-error input",
            ".Mui-error textarea",
            '.Mui-error [role="combobox"]',
          ].join(",")
        )
      );
      const visibleInvalidFields = candidates.filter((candidate) => candidate.offsetParent !== null);
      const messages = visibleInvalidFields.flatMap((field) => {
        const describedByIds = field.getAttribute("aria-describedby")?.split(/\s+/) ?? [];
        const describedByMessage = describedByIds
          .map((id) => document.getElementById(id)?.textContent?.trim())
          .find(Boolean);
        const helperMessage = field
          .closest(".MuiFormControl-root")
          ?.querySelector<HTMLElement>(".MuiFormHelperText-root.Mui-error")
          ?.textContent?.trim();
        const fieldId = field.getAttribute("id");
        const label =
          (fieldId ? document.querySelector<HTMLLabelElement>(`label[for="${CSS.escape(fieldId)}"]`) : null)
            ?.textContent?.replace(/\s*\*$/, "")
            .trim() ??
          field.closest(".MuiFormControl-root")?.querySelector("label")?.textContent?.replace(/\s*\*$/, "").trim();
        const validationMessage = describedByMessage ?? helperMessage;

        if (!validationMessage) {
          return label ? [`${label} is missing or incorrect.`] : [];
        }

        return label && !validationMessage.toLowerCase().includes(label.toLowerCase())
          ? [`${label}: ${validationMessage}`]
          : [validationMessage];
      });
      setFieldErrorMessages(Array.from(new Set(messages)));

      const firstInvalidField = visibleInvalidFields[0];
      if (firstInvalidField) {
        firstInvalidField.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
        firstInvalidField.focus({ preventScroll: true });
      }
    };

    // Validation errors are usually stored immediately after the toast is dispatched.
    // Wait for that render before looking for the first invalid control.
    const frameId = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(scrollToFirstInvalidField);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [inlineError, inlineTarget]);

  const handleClose = (event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const handleExited = () => {
    setMessageInfo(undefined);
  };

  const inlineBanner = inlineError && inlineTarget
    ? createPortal(
        <FormErrorBanner
          open
          title={inlineError.title ?? (fieldErrorMessages.length ? "Missing or incorrect information" : "Save failed")}
          message={
            inlineError.message === "An error has occured"
              ? fieldErrorMessages.length
                ? (
                    <ul style={{ margin: 0, paddingLeft: "20px" }}>
                      {fieldErrorMessages.map((message) => <li key={message}>{message}</li>)}
                    </ul>
                  )
                : "Unable to complete this action. Please try again."
              : inlineError.message
          }
          onDismiss={() => {
            setFieldErrorMessages([]);
            setInlineError(undefined);
            setInlineTarget(undefined);
          }}
          sx={{ mt: 2 }}
        />,
        inlineTarget
      )
    : null;

  return (
    <>
      {inlineBanner}
      <MuiSnackBar
        key={messageInfo ? messageInfo.key : undefined}
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        TransitionProps={{ onExited: handleExited }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ marginTop: 2 }}
        TransitionComponent={Zoom}
      >
        <Alert
          onClose={handleClose}
          severity={messageInfo?.alertColor}
          variant="standard"
          sx={{
            fontWeight: theme.typography.fontWeightBold,
            borderRadius: "50px",
            border: "1px solid",
            boxShadow: "0px 2px 8px rgba(0,0,0,1)",
            width: "100%",
            alignItems: "center",
          }}
        >
          {messageInfo?.message}
        </Alert>
      </MuiSnackBar>
    </>
  );
}
